import {
  Button,
  cn,
  DataTable,
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTableSkeleton,
  DataTableViewOptions,
  Input,
  useDataTable,
} from '@data-delivery/d3s';
import type { AssignmentEntity, UserEntity } from '@data-delivery/permission-ui-api';
import {
  assignmentsQueries,
  useBusinessGroupsRQ,
} from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ComposableSheet } from '../sheets/composable-sheet';

export interface UserAssignmentsViewProps {
  user: UserEntity;
  tableClassName?: string;
  hideHeading?: boolean;
}

export function UserAssignmentsView({
  user,
  tableClassName,
  hideHeading,
}: UserAssignmentsViewProps) {
  const { businessGroupId } = useParams<{ businessGroupId?: string }>();
  const { data: businessGroups } = useBusinessGroupsRQ();

  const bgAssignmentsQuery = useQuery(
    businessGroupId
      ? assignmentsQueries.byBusinessGroup(businessGroupId)
      : assignmentsQueries.byBusinessGroup(''),
  );
  const listAssignmentsQuery = useQuery(assignmentsQueries.list());
  const isLoading = businessGroupId ? bgAssignmentsQuery.isLoading : listAssignmentsQuery.isLoading;
  const isError = businessGroupId ? bgAssignmentsQuery.isError : listAssignmentsQuery.isError;
  const error = businessGroupId ? bgAssignmentsQuery.error : listAssignmentsQuery.error;
  const assignmentsData = businessGroupId ? bgAssignmentsQuery.data : listAssignmentsQuery.data;

  const userAssignedIds = useMemo(() => new Set((user.assignments || []).map(a => a)), [user]);

  const bgNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    businessGroups?.forEach(bg => {
      map.set(bg.id, bg.name || bg.shortName || bg.id);
    });
    return map;
  }, [businessGroups]);

  const userRows = useMemo(() => {
    let all: AssignmentEntity[] = [];
    const raw = assignmentsData as unknown;
    if (Array.isArray(raw)) {
      all = raw as AssignmentEntity[];
    } else if (raw && typeof raw === 'object' && 'users' in raw) {
      const maybeUsers = (raw as { users?: unknown }).users;
      if (Array.isArray(maybeUsers)) {
        all = maybeUsers as AssignmentEntity[];
      }
    }

    return all.filter(a => userAssignedIds.has(a.id));
  }, [assignmentsData, userAssignedIds]);

  const uniqueBusinessGroupIds = useMemo(() => {
    if (businessGroupId) {
      return [];
    }

    return Array.from(new Set(userRows.map(a => a.businessGroup)))
      .filter(Boolean)
      .sort();
  }, [userRows, businessGroupId]);

  const [searchTerm, setSearchTerm] = useState('');
  const rows = useMemo(() => {
    if (!searchTerm.trim()) {
      return userRows;
    }

    const needle = searchTerm.toLowerCase();
    return userRows.filter(r => {
      const searchFields = [r.id, r.name, r.description, r.contract];

      if (!businessGroupId && r.businessGroup) {
        const bgName = bgNamesMap.get(r.businessGroup);
        if (bgName) {
          searchFields.push(bgName);
        }
      }

      return searchFields.filter(Boolean).some(v => String(v).toLowerCase().includes(needle));
    });
  }, [userRows, searchTerm, businessGroupId, bgNamesMap]);
  type Row = AssignmentEntity;

  const columns = useMemo<Array<ColumnDef<Row>>>(
    () => [
      {
        accessorKey: 'id',
        header: ({ column }) => <DataTableColumnHeader column={column} title='ID' />,
        cell: ({ getValue }) => <span className='font-medium'>{getValue<string>()}</span>,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
        cell({ getValue, row }) {
          const name = getValue<string>();
          if (businessGroupId) {
            return (
              <Link
                to={`/business-groups/${encodeURIComponent(
                  businessGroupId,
                )}/assignments/${encodeURIComponent(row.original.id)}`}
                className='text-primary hover:underline font-medium'
              >
                {name}
              </Link>
            );
          }

          const rawBg = row.original.businessGroup;
          const slug = `bg_name_${encodeURIComponent(rawBg)}:assign_name_${encodeURIComponent(
            row.original.name || row.original.id,
          )}`;
          return (
            <Link
              to={`/configuration/assignments/${slug}/detail`}
              className='text-primary hover:underline font-medium'
            >
              {name}
            </Link>
          );
        },
      },
      ...(businessGroupId
        ? []
        : [
            {
              accessorKey: 'businessGroup',
              header: ({ column }) => (
                <DataTableColumnHeader column={column} title='Business Group' />
              ),
              cell({ getValue, row }) {
                const bgId = getValue<string | undefined>();
                const displayName = bgId ? bgNamesMap.get(bgId) || bgId : '—';

                if (!bgId || bgId === '—') {
                  return <span>{displayName}</span>;
                }

                return (
                  <Link
                    to={`/business-groups/${encodeURIComponent(
                      bgId,
                    )}/assignments/${encodeURIComponent(row.original.id)}`}
                    className='text-primary hover:underline font-medium'
                  >
                    {displayName}
                  </Link>
                );
              },
              filterFn(row, columnId, filterValues) {
                if (!filterValues || filterValues.length === 0) {
                  return true;
                }

                const raw = row.getValue(columnId);
                const value = typeof raw === 'string' ? raw : undefined;
                return value ? filterValues.includes(value) : false;
              },
            } satisfies ColumnDef<Row>,
          ]),
      {
        accessorKey: 'description',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Description' />,
        cell: ({ getValue }) => (
          <span className='truncate max-w-[240px]'>{getValue<string | undefined>() || '—'}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'contract',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Contract' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        id: 'startDate',
        accessorFn: r => r.startDate ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Start Date' />,
        cell: ({ row }) => (
          <span>
            {row.original.startDate ? new Date(row.original.startDate).toLocaleDateString() : '—'}
          </span>
        ),
        sortingFn: 'basic',
      },
      {
        id: 'endDate',
        accessorFn: r => r.endDate ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} title='End Date' />,
        cell: ({ row }) => (
          <span>
            {row.original.endDate ? new Date(row.original.endDate).toLocaleDateString() : '—'}
          </span>
        ),
        sortingFn: 'basic',
      },
      {
        id: 'lastModified',
        accessorFn: r => r.lastModified ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Last Modified' />,
        cell: ({ row }) => (
          <span>
            {row.original.lastModified ? new Date(row.original.lastModified).toLocaleString() : '—'}
          </span>
        ),
        sortingFn: 'basic',
      },
    ],
    [businessGroupId, bgNamesMap],
  );

  const table = useDataTable<Row>({
    data: rows,
    columns,
    getRowId: r => r.id,
    initialState: {
      columnVisibility: { id: false },
    },
  });

  const allLeafColumns = table.getAllLeafColumns();

  const handleExportCsv = () => {
    const visibleCols = allLeafColumns.filter(c => c.getIsVisible());
    const header = visibleCols.map(c => c.id || c.columnDef.header?.toString() || '').join(',');
    const body = table
      .getRowModel()
      .rows.map(r =>
        visibleCols
          .map(c => {
            const colId = c.id;
            const v = colId ? r.getValue(colId) : undefined;
            const cell = typeof v === 'string' ? v : v == null ? '' : String(v);
            const escaped = '"' + cell.replace(/"/g, '""') + '"';
            return escaped;
          })
          .join(','),
      )
      .join('\n');
    const csv = header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-assignments-${user.loginId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className='flex flex-col gap-4'>
      {!hideHeading && <h2 className='text-base font-semibold'>Assignments for {user.loginId}</h2>}
      <div className='flex flex-wrap gap-2 items-center'>
        <Input
          type='text'
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
          }}
          placeholder={
            businessGroupId
              ? 'Search assignments (id, name, description, contract)'
              : 'Search assignments (id, name, business group, description, contract)'
          }
          className='h-9 w-full max-w-xs rounded-md border px-2 text-sm'
        />
        {!businessGroupId && uniqueBusinessGroupIds.length > 0 && (
          <DataTableFacetedFilter
            column={table.getColumn('businessGroup')}
            title='Business Group'
            options={uniqueBusinessGroupIds.map(bgId => ({
              label: bgNamesMap.get(bgId) || bgId,
              value: bgId,
            }))}
          />
        )}
        <DataTableViewOptions table={table} />
        <Button variant='outline' size='sm' onClick={handleExportCsv}>
          Export CSV
        </Button>
      </div>
      {isLoading ? (
        <DataTableSkeleton
          withPagination={false}
          rowCount={8}
          columnCount={columns.length}
          searchableColumnCount={0}
          filterableColumnCount={0}
          showViewOptions={false}
          cellWidths={['3rem', '10rem', '16rem', '8rem', '8rem', '8rem', '8rem', '12rem']}
          shrinkZero
        />
      ) : isError ? (
        <div className='text-destructive text-sm'>
          Failed to load assignments:{' '}
          {(() => {
            const err = error as unknown;
            if (err && typeof err === 'object' && 'message' in err) {
              const msg = (err as { message?: string }).message;
              return msg || 'Unknown error';
            }

            return 'Unknown error';
          })()}
        </div>
      ) : rows.length === 0 ? (
        <div className='text-sm text-muted-foreground'>
          {businessGroupId
            ? 'User has no assignments in this Business Group.'
            : 'User has no assignments.'}
        </div>
      ) : (
        <div className='flex flex-col rounded-md border'>
          <DataTable
            table={table}
            className={cn('h-[calc(100vh-300px)]', tableClassName)}
            emptyPlaceholderTitle='Assignments'
            emptyPlaceholderDescription={
              businessGroupId
                ? 'User has no assignments in this Business Group'
                : 'User has no assignments'
            }
          />
        </div>
      )}
    </div>
  );
}

export function UserAssignmentsSheet({ user }: UserAssignmentsViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const handleClose = () => {
    const state = location.state as { backgroundLocation?: Location } | undefined;
    if (state?.backgroundLocation) {
      navigate(state.backgroundLocation.pathname, { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <ComposableSheet
      open
      onOpenChange={open => {
        if (!open) {
          handleClose();
        }
      }}
      title={`Assignments: ${user.loginId}`}
      description='Assignments linked to this user'
      side='right'
      widthClassName='w-full sm:max-w-5xl'
      footer={({ close }) => (
        <Button variant='outline' onClick={close}>
          Close
        </Button>
      )}
    >
      <UserAssignmentsView user={user} />
    </ComposableSheet>
  );
}
